/**
 * Creates a storage object that can save a maximum of maxSize items.
 * When a save exceeds the size, the oldest entry is removed to make space.
 * @param {Object} config
 * @param {Number} config.maxSize The max size to store items.
 * @returns
 */
const createCycleStorage = (config = {}) => {
  let { maxSize = 3 } = config;
  let datasets = {};

  return {
    /**
     * Loads the result of dump (serialized state).
     */
    load(data) {
      const { maxSize: max, datasets: ds } = data;
      maxSize = max;
      datasets = { ...ds };
    },

    /**
     * Dump exports the state so it can be loaded via `load()`.
     */
    dump() {
      return { maxSize, datasets };
    },

    /**
     * Clears the internal store of datasets.
     */
    clear() {
      datasets = {};
    },

    /**
     * Returns all of the datasets sorted from old to new.
     */
    all() {
      const keys = Object.keys(datasets)
        .map(k => parseInt(k, 10))
        .sort((a, b) => a - b);

      const o = {};
      keys.forEach(k => {
        o[k] = datasets[k];
      });

      return Object.values(o);
    },

    /**
     * Returns the last (most recently stored) dataset.
     */
    last() {
      const keys = Object.keys(datasets)
        .map(k => parseInt(k, 10))
        .sort((a, b) => a - b);
      const lastKey = [...keys].pop();
      return datasets[lastKey];
    },

    /**
     * Saves the given data as a dataset (an additional dataset entry).
     * The data can be in any format as long as it's serializable.
     */
    save(data) {
      let newDatasets = {
        ...datasets,
      };

      const keys = Object.keys(newDatasets)
        .map(k => parseInt(k, 10))
        .sort((a, b) => a - b);

      if (keys.length === 0) {
        newDatasets[0] = data;
        datasets = newDatasets;
        return newDatasets;
      }

      let lastIndex = parseInt(keys[keys.length - 1], 10);

      // Failsafe after parsing to make sure index is always
      // a number and safe to be incremented later.
      if (!lastIndex && lastIndex !== 0) {
        lastIndex = -1;
      }

      if (keys.length >= maxSize) {
        // Make room for new data, remove oldest entry.
        delete newDatasets[keys[0]];
      }

      newDatasets = {
        ...newDatasets,
        [lastIndex + 1]: data,
      };

      // Important to set the new object reference instead of mutating existing.
      datasets = newDatasets;

      return datasets;
    },
  };
};

export default createCycleStorage;
